import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EncrDecrService } from '@mysrc/app/Providers/EncrDecrService';
import { API_ENDPOINTS } from '@mysrc/app/api-endpoints';
import { BaseHttpReqRespService } from '@mysrc/app/modules/fwcore/services/base-http-req-resp.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { CommonService } from '../../question/services/commonservice';
import {
  ServiceLineSurveyMap,
  ServiceLineServiceProviderMap,
  UserDetail,
  StatusDetail,
  DocumentDetail,
  MasterRespondents,
  Language,
  SurveyServiceLineLanguageMappedDetail
} from '../models/manage-service-line.model';

@Injectable()
export class ManageServiceLineService extends BaseHttpReqRespService {
  myAppUrl: any;
  MSL_ENDPOINT = API_ENDPOINTS.MANAGE_SERVICE_LINE;

  constructor(
    http: HttpClient,
    @Inject(OKTA_AUTH) private oauth: OktaAuth,
    private EncrDecr: EncrDecrService,
    private commonService: CommonService,
    @Inject('CONFIG_SETTINGS') configSettings: any
  ) {
    super(http, oauth);
    const genSettings = configSettings[<any>'general'];
    this.baseUrl = genSettings[<any>'adminApiUrl'];
    this.apiKey = genSettings[<any>'apiKey'];
    this.myAppUrl = this.baseUrl;
  }

  // ── GET: Mapped surveys ────────────────────────────────────────────────────
  getMappedSurveyList(serviceLineId: string, companyId: string): Observable<ServiceLineSurveyMap[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.MAPPED_SURVEY_LIST}?serviceLineId=${serviceLineId}&companyId=${companyId}`
    );
  }

  // ── GET: Unmapped surveys ──────────────────────────────────────────────────
  getUnMappedSurveyList(serviceLineId: string, workspaceId: string, companyId: string): Observable<ServiceLineSurveyMap[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.UNMAPPED_SURVEY_LIST}?serviceLineId=${serviceLineId}&workspaceId=${workspaceId}&companyId=${companyId}`
    );
  }

  // ── POST: Map surveys ──────────────────────────────────────────────────────
  mapSurveyToServiceLine(serviceLineId: string, surveyMapIdList: string, companyId: string): Observable<ServiceLineSurveyMap[]> {
    const params = `serviceLineId=${serviceLineId}&surveyMapIdList=${surveyMapIdList}&companyId=${companyId}`;
    return this.http.post<ServiceLineSurveyMap[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.MAP_SURVEY}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Unmap surveys ────────────────────────────────────────────────────
  unMapSurveyFromServiceLine(serviceLineId: string, surveyUnMapIdList: string, companyId: string): Observable<ServiceLineSurveyMap[]> {
    const params = `serviceLineId=${serviceLineId}&surveyUnMapIdList=${surveyUnMapIdList}&companyId=${companyId}`;
    return this.http.post<ServiceLineSurveyMap[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.UNMAP_SURVEY}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── GET: Mapped service providers ─────────────────────────────────────────
  getMappedServiceProviderList(serviceLineId: string): Observable<ServiceLineServiceProviderMap[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.MAPPED_PROVIDER_LIST}?serviceLineId=${serviceLineId}`
    );
  }

  // ── GET: Unmapped service providers ───────────────────────────────────────
  getUnMappedServiceProviderList(serviceLineId: string, workspaceId: string): Observable<ServiceLineServiceProviderMap[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.UNMAPPED_PROVIDER_LIST}?serviceLineId=${serviceLineId}&workspaceId=${workspaceId}`
    );
  }

  // ── POST: Map service providers ───────────────────────────────────────────
  mapServiceProviderToServiceLine(serviceLineId: string, serviceProviderMapIdList: string): Observable<ServiceLineServiceProviderMap[]> {
    const params = `serviceLineId=${serviceLineId}&serviceProviderMapIdList=${serviceProviderMapIdList}`;
    return this.http.post<ServiceLineServiceProviderMap[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.MAP_PROVIDER}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Unmap service providers ─────────────────────────────────────────
  unMapServiceProviderFromServiceLine(serviceLineId: string, serviceProviderUnMapIdList: string): Observable<ServiceLineServiceProviderMap[]> {
    const params = `serviceLineId=${serviceLineId}&serviceProviderUnMapIdList=${serviceProviderUnMapIdList}`;
    return this.http.post<ServiceLineServiceProviderMap[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.UNMAP_PROVIDER}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Delete survey mapping ───────────────────────────────────────────
  deleteSurveyServiceLine(serviceLineId: number, surveyId: number, companyId: string): Observable<any> {
    const params = `serviceLineId=${serviceLineId}&surveyId=${surveyId}&companyId=${companyId}`;
    return this.http.post<any>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.DELETE_SURVEY_SERVICE_LINE}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Delete provider mapping ─────────────────────────────────────────
  deleteCompanyServiceLine(serviceLineId: number, companyId: number): Observable<any> {
    const params = `serviceLineId=${serviceLineId}&companyId=${companyId}`;
    return this.http.post<any>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.DELETE_COMPANY_SERVICE_LINE}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── GET: Edit schedule ────────────────────────────────────────────────────
  editSurveySchedule(scheduleId: number, surveyId: number, serviceLineId: string): Observable<any> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.EDIT_SURVEY_SCHEDULE}?scheduleId=${scheduleId}&surveyId=${surveyId}&serviceLineId=${serviceLineId}`
    );
  }

  // ── POST: Save schedule ───────────────────────────────────────────────────
  saveSurveySchedule(model: any, companyId: string): Observable<ServiceLineSurveyMap[]> {
    const userId = this.EncrDecr.get(localStorage.getItem('userId') ?? '');
    return this.http.post<ServiceLineSurveyMap[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.SAVE_SURVEY_SCHEDULE}`,
      model,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        params: { companyId }
      }
    );
  }

  // ── POST: Copy survey ─────────────────────────────────────────────────────
  copySurvey(model: any): Observable<ServiceLineSurveyMap[]> {
    const workspaceId = this.EncrDecr.get(localStorage.getItem('workspaceId') ?? '');
    const userId = this.EncrDecr.get(localStorage.getItem('userId') ?? '');
    const companyId = this.EncrDecr.get(localStorage.getItem('companyId') ?? '');
    return this.http.post<ServiceLineSurveyMap[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.COPY_SURVEY}`,
      model,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        params: { workSpaceId: workspaceId, userId, companyId }
      }
    );
  }

  // ── POST: Update survey map (show/hide) ───────────────────────────────────
  updateSurveyMap(serviceLineId: string, surveyId: number, isShow: boolean, companyId: string): Observable<ServiceLineSurveyMap[]> {
    const params = `serviceLineId=${serviceLineId}&surveyId=${surveyId}&isShow=${isShow}&companyId=${companyId}`;
    return this.http.post<ServiceLineSurveyMap[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.UPDATE_SURVEY_MAP}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Save template survey settings ───────────────────────────────────
  saveTemplate(params: Record<string, any>): Observable<string> {
    return this.http.post<string>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.SAVE_TEMPLATE}`,
      '',
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
        params
      }
    );
  }

  // ── GET: Respondents ──────────────────────────────────────────────────────
  getRespondents(serviceLineId: string, companyId: string): Observable<MasterRespondents> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.GET_RESPONDENTS}?serviceLineId=${serviceLineId}&companyId=${companyId}`
    );
  }

  // ── POST: Save respondent type ────────────────────────────────────────────
  saveRespondentType(serviceLineId: string, companyId: string, respondentId: number): Observable<string> {
    const params = `serviceLineId=${serviceLineId}&companyId=${companyId}&respondentId=${respondentId}`;
    return this.http.post<string>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.SAVE_RESPONDENT_TYPE}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Get users by company ────────────────────────────────────────────
  getAllUsersByCompany(serviceLineId: number, companyId: number, surveyId: number): Observable<UserDetail[]> {
    const params = `ServiceLineId=${serviceLineId}&CompanyId=${companyId}&surveyId=${surveyId}`;
    return this.http.post<UserDetail[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.GET_ALL_USERS_BY_COMPANY}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Update exceptional user ─────────────────────────────────────────
  updateExceptionalUser(serviceLineId: number, companyId: number, surveyId: number, isExceptionalUser: boolean, userId: number): Observable<UserDetail[]> {
    const params = `ServiceLineId=${serviceLineId}&CompanyId=${companyId}&SurveyId=${surveyId}&isExceptionalUser=${isExceptionalUser}&UserId=${userId}`;
    return this.http.post<UserDetail[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.UPDATE_EXCEPTIONAL_USER}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Get status list ─────────────────────────────────────────────────
  getAllStatus(): Observable<StatusDetail[]> {
    return this.http.post<StatusDetail[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.GET_ALL_STATUS}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── GET: Documents ────────────────────────────────────────────────────────
  getDocumentList(surveyId: number, serviceLineId: string): Observable<DocumentDetail[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.GET_DOCUMENT_LIST}?surveyId=${surveyId}&serviceLineId=${serviceLineId}`
    );
  }

  // ── POST: Delete document ─────────────────────────────────────────────────
  deleteDocument(surveyId: string, documentId: number): Observable<void> {
    const params = `serveyId=${surveyId}&documentId=${documentId}`;
    return this.http.post<void>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.DELETE_DOCUMENT}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: Format download ─────────────────────────────────────────────────
  formatDownload(serviceLineId: number, surveyId: number, companyId: number): Observable<any[]> {
    const params = `serviceLineId=${serviceLineId}&surveyId=${surveyId}&companyId=${companyId}`;
    return this.http.post<any[]>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.FORMAT_DOWNLOAD}?${params}`,
      null,
      { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
    );
  }

  // ── POST: User download ───────────────────────────────────────────────────
  userDownload(companyIds: number[], surveyId: number, serviceLineId: string): Observable<Blob> {
    return this.http.post<Blob>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.USER_DOWNLOAD}`,
      { companyIds, surveyId, serviceLineId },
      { responseType: 'blob' as 'json' }
    );
  }

  // ── POST: Upload answers ──────────────────────────────────────────────────
  uploadAnswers(model: any): Observable<string> {
    return this.http.post<string>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.UPLOAD_ANSWERS}`,
      model,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=UTF-8' }) }
    );
  }

  // ── GET: Email details ────────────────────────────────────────────────────
  getEmailDetails(serviceLineId: string): Observable<any> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.GET_EMAIL_DETAILS}?serviceLineId=${serviceLineId}`
    );
  }

  // ── POST: Save email config ───────────────────────────────────────────────
  saveEmailConfiguration(model: any): Observable<any> {
    return this.http.post<any>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.SAVE_EMAIL_DETAILS}`,
      model,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    );
  }

  // ── GET: Language list ────────────────────────────────────────────────────
  getLanguageList(): Observable<Language[]> {
    return this.commonService.httpGet('', `${this.myAppUrl}/${this.MSL_ENDPOINT.GET_LANGUAGE_LIST}`);
  }

  // ── GET: Survey language mapped list ─────────────────────────────────────
  getSurveyServiceLineLanguageMappedList(surveyId: number, serviceLineId: string): Observable<SurveyServiceLineLanguageMappedDetail[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.LANGUAGE_MAPPED_LIST}?surveyId=${surveyId}&serviceLineId=${serviceLineId}`
    );
  }

  // ── POST: Save survey provider language settings ──────────────────────────
  saveSurveyProviderLanguage(model: any): Observable<any> {
    return this.http.post<any>(
      `${this.myAppUrl}/${this.MSL_ENDPOINT.SAVE_PROVIDER_LANGUAGE}`,
      model,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
    );
  }

  // ── GET: Delete survey company language ───────────────────────────────────
  deleteSurveyCompanyLanguage(surveyId: string, serviceLineId: string, mappingId: string): Observable<SurveyServiceLineLanguageMappedDetail[]> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.DELETE_SURVEY_COMPANY_LANGUAGE}?surveyId=${surveyId}&serviceLineId=${serviceLineId}&mappingId=${mappingId}`
    );
  }

  // ── GET: Is Consip service line ───────────────────────────────────────────
  isConsipServiceLine(serviceLineId: string, workspaceId: string, companyId: string): Observable<boolean> {
    return this.commonService.httpGet(
      '',
      `${this.myAppUrl}/${this.MSL_ENDPOINT.IS_CONSIP}?serviceLineId=${serviceLineId}&workspaceId=${workspaceId}&companyId=${companyId}`
    );
  }
}
